/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import {
  X,
  Briefcase,
  Calendar,
  User,
  Phone,
  Mail,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
} from "lucide-react";

const WorkHistoryModal = ({ isOpen, onClose, caregiverId, onFetchHistory }) => {
  const [loading, setLoading] = useState(false);
  const [workHistory, setWorkHistory] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    if (isOpen && caregiverId) {
      fetchWorkHistory();
    }
  }, [isOpen, caregiverId]);

  const fetchWorkHistory = async () => {
    setLoading(true);
    try {
      const history = await onFetchHistory(caregiverId);
      setWorkHistory(history);
    } catch (error) {
      console.error("Error fetching work history:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const getStatusColor = (status, type) => {
    if (type === "contract") {
      switch (status) {
        case "ACTIVE":
          return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
        case "COMPLETED":
          return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
        case "TERMINATED":
          return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
        case "PAUSED":
          return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400";
        default:
          return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
      }
    } else if (type === "booking") {
      switch (status) {
        case "Completed":
          return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
        case "Work_Started":
          return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
        case "In_Progress":
          return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400";
        case "Cancelled":
          return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
        default:
          return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
      }
    } else if (type === "job") {
      switch (status) {
        case "completed":
          return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
        case "work_started":
          return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
        case "assigned":
          return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400";
        case "cancelled":
          return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
        default:
          return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
      }
    }
  };

  const getStatusIcon = (status, type) => {
    if (type === "contract") {
      switch (status) {
        case "ACTIVE":
          return <CheckCircle size={14} className="text-emerald-500" />;
        case "COMPLETED":
          return <CheckCircle size={14} className="text-blue-500" />;
        case "TERMINATED":
          return <XCircle size={14} className="text-red-500" />;
        case "PAUSED":
          return <AlertCircle size={14} className="text-yellow-500" />;
        default:
          return <Clock size={14} className="text-gray-500" />;
      }
    } else if (type === "booking") {
      switch (status) {
        case "Completed":
          return <CheckCircle size={14} className="text-emerald-500" />;
        case "Work_Started":
          return <Clock size={14} className="text-blue-500" />;
        case "In_Progress":
          return <Clock size={14} className="text-yellow-500" />;
        case "Cancelled":
          return <XCircle size={14} className="text-red-500" />;
        default:
          return <Clock size={14} className="text-gray-500" />;
      }
    } else if (type === "job") {
      switch (status) {
        case "completed":
          return <CheckCircle size={14} className="text-emerald-500" />;
        case "work_started":
          return <Clock size={14} className="text-blue-500" />;
        case "assigned":
          return <Clock size={14} className="text-yellow-500" />;
        case "cancelled":
          return <XCircle size={14} className="text-red-500" />;
        default:
          return <Clock size={14} className="text-gray-500" />;
      }
    }
  };

  const renderContracts = () => {
    if (!workHistory?.contracts?.length) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-dark-muted">
          No contracts found
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {workHistory.contracts.map((contract) => (
          <div
            key={contract.id}
            className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Briefcase size={18} className="text-primary" />
                <h4 className="font-bold text-text dark:text-dark-text">
                  Contract #{contract.id.slice(0, 8)}
                </h4>
              </div>
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${getStatusColor(
                  contract.status,
                  "contract",
                )}`}
              >
                {getStatusIcon(contract.status, "contract")}
                {contract.status}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <User size={14} />
                <span>
                  Client:{" "}
                  {contract.client?.name || contract.user?.name || "N/A"}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <Phone size={14} />
                <span>
                  {contract.client?.phoneNumber ||
                    contract.user?.phoneNumber ||
                    "N/A"}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <Calendar size={14} />
                <span>Start: {formatDate(contract.startDate)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <Calendar size={14} />
                <span>
                  End:{" "}
                  {contract.endDate ? formatDate(contract.endDate) : "Ongoing"}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <DollarSign size={14} />
                <span>Monthly: Br {contract.monthlyAmount || "N/A"}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <FileText size={14} />
                <span>Platform: {contract.platformPercentage || 20}%</span>
              </div>
            </div>

            {contract.terminationReason && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  <strong>Termination Reason:</strong>{" "}
                  {contract.terminationReason}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderBookings = () => {
    if (!workHistory?.bookings?.length) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-dark-muted">
          No bookings found
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {workHistory.bookings.map((booking) => (
          <div
            key={booking.id}
            className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-primary" />
                <h4 className="font-bold text-text dark:text-dark-text">
                  Booking #{booking.id.slice(0, 8)}
                </h4>
              </div>
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${getStatusColor(
                  booking.status,
                  "booking",
                )}`}
              >
                {getStatusIcon(booking.status, "booking")}
                {booking.status.replace("_", " ")}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <User size={14} />
                <span>Client: {booking.user?.name || "N/A"}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <Phone size={14} />
                <span>{booking.user?.phoneNumber || "N/A"}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <Calendar size={14} />
                <span>Date: {formatDate(booking.date)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <Clock size={14} />
                <span>
                  Slot: {booking.slotDate} at {booking.slotTime || "N/A"}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <DollarSign size={14} />
                <span>Amount: Br {booking.agreedAmount || "N/A"}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <FileText size={14} />
                <span>
                  Category:{" "}
                  {booking.serviceCategory?.label || booking.category || "N/A"}
                </span>
              </div>
            </div>

            {booking.cancellationReason && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  <strong>Cancellation Reason:</strong>{" "}
                  {booking.cancellationReason}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderJobs = () => {
    if (!workHistory?.jobs?.length) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-dark-muted">
          No jobs found
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {workHistory.jobs.map((job) => (
          <div
            key={job.id}
            className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Briefcase size={18} className="text-primary" />
                <h4 className="font-bold text-text dark:text-dark-text">
                  {job.title || `Job #${job.id.slice(0, 8)}`}
                </h4>
              </div>
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${getStatusColor(
                  job.workState,
                  "job",
                )}`}
              >
                {getStatusIcon(job.workState, "job")}
                {job.workState.replace("_", " ")}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <User size={14} />
                <span>Client: {job.createdBy?.name || "N/A"}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <Phone size={14} />
                <span>{job.createdBy?.phoneNumber || "N/A"}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <Calendar size={14} />
                <span>Posted: {formatDate(job.createdAt)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <Calendar size={14} />
                <span>Assigned: {formatDate(job.assignedAt) || "N/A"}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <DollarSign size={14} />
                <span>Budget: Br {job.budget || "N/A"}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-dark-muted">
                <FileText size={14} />
                <span>Category: {job.serviceCategory?.label || "N/A"}</span>
              </div>
            </div>

            {job.description && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  <strong>Description:</strong> {job.description}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderAllHistory = () => {
    const allItems = [
      ...(workHistory?.contracts || []).map((item) => ({
        ...item,
        type: "contract",
        date: item.startDate,
      })),
      ...(workHistory?.bookings || []).map((item) => ({
        ...item,
        type: "booking",
        date: item.date,
      })),
      ...(workHistory?.jobs || []).map((item) => ({
        ...item,
        type: "job",
        date: item.createdAt,
      })),
    ].sort((a, b) => new Date(b.date) - new Date(a.date));

    if (!allItems.length) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-dark-muted">
          No work history found
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {allItems.map((item) => {
          if (item.type === "contract") {
            return (
              <div
                key={`contract-${item.id}`}
                className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Briefcase size={18} className="text-primary" />
                    <h4 className="font-bold text-text dark:text-dark-text">
                      Contract #{item.id.slice(0, 8)}
                    </h4>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${getStatusColor(
                      item.status,
                      "contract",
                    )}`}
                  >
                    {getStatusIcon(item.status, "contract")}
                    {item.status}
                  </span>
                </div>
                <div className="text-sm text-gray-600 dark:text-dark-muted">
                  Client: {item.client?.name || item.user?.name || "N/A"} •
                  Start: {formatDate(item.startDate)}
                </div>
              </div>
            );
          } else if (item.type === "booking") {
            return (
              <div
                key={`booking-${item.id}`}
                className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Calendar size={18} className="text-primary" />
                    <h4 className="font-bold text-text dark:text-dark-text">
                      Booking #{item.id.slice(0, 8)}
                    </h4>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${getStatusColor(
                      item.status,
                      "booking",
                    )}`}
                  >
                    {getStatusIcon(item.status, "booking")}
                    {item.status.replace("_", " ")}
                  </span>
                </div>
                <div className="text-sm text-gray-600 dark:text-dark-muted">
                  Client: {item.user?.name || "N/A"} • Date:{" "}
                  {formatDate(item.date)}
                </div>
              </div>
            );
          } else if (item.type === "job") {
            return (
              <div
                key={`job-${item.id}`}
                className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Briefcase size={18} className="text-primary" />
                    <h4 className="font-bold text-text dark:text-dark-text">
                      {item.title || `Job #${item.id.slice(0, 8)}`}
                    </h4>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${getStatusColor(
                      item.workState,
                      "job",
                    )}`}
                  >
                    {getStatusIcon(item.workState, "job")}
                    {item.workState.replace("_", " ")}
                  </span>
                </div>
                <div className="text-sm text-gray-600 dark:text-dark-muted">
                  Client: {item.createdBy?.name || "N/A"} • Posted:{" "}
                  {formatDate(item.createdAt)}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const getTotalCount = () => {
    return (
      (workHistory?.contracts?.length || 0) +
      (workHistory?.bookings?.length || 0) +
      (workHistory?.jobs?.length || 0)
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
          <div>
            <h2 className="text-2xl font-bold text-text dark:text-dark-text">
              Work History
            </h2>
            <p className="text-sm text-gray-500 dark:text-dark-muted mt-1">
              Complete work history for this caregiver
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          >
            <X size={24} className="text-gray-500 dark:text-dark-muted" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-6 pt-4 border-b border-gray-200 dark:border-dark-border">
          <button
            onClick={() => setActiveTab("all")}
            className={`px-4 py-2 font-semibold text-sm rounded-t-xl transition-colors ${
              activeTab === "all"
                ? "bg-primary text-white"
                : "text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-gray-700"
            }`}
          >
            All ({getTotalCount()})
          </button>
          <button
            onClick={() => setActiveTab("contracts")}
            className={`px-4 py-2 font-semibold text-sm rounded-t-xl transition-colors ${
              activeTab === "contracts"
                ? "bg-primary text-white"
                : "text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-gray-700"
            }`}
          >
            Contracts ({workHistory?.contracts?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab("bookings")}
            className={`px-4 py-2 font-semibold text-sm rounded-t-xl transition-colors ${
              activeTab === "bookings"
                ? "bg-primary text-white"
                : "text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-gray-700"
            }`}
          >
            Bookings ({workHistory?.bookings?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab("jobs")}
            className={`px-4 py-2 font-semibold text-sm rounded-t-xl transition-colors ${
              activeTab === "jobs"
                ? "bg-primary text-white"
                : "text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-gray-700"
            }`}
          >
            Jobs ({workHistory?.jobs?.length || 0})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {activeTab === "all" && renderAllHistory()}
              {activeTab === "contracts" && renderContracts()}
              {activeTab === "bookings" && renderBookings()}
              {activeTab === "jobs" && renderJobs()}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-dark-border">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl font-semibold bg-gray-100 dark:bg-gray-700 text-text dark:text-dark-text hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkHistoryModal;
